import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Trash2, Edit2, X, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { authenticatedGet, authenticatedPost, authenticatedPut, authenticatedDelete } from '../utils/apiUtils';
import { getAccessToken } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

interface Comment {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  username: string;
}

interface CommentSectionProps {
  willId: string;
  onClose?: () => void;
  isModal?: boolean;
  initialCommentsToShow?: number;
}

const CommentSection: React.FC<CommentSectionProps> = ({ 
  willId, 
  onClose, 
  isModal = false,
  initialCommentsToShow = 5 
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [displayedComments, setDisplayedComments] = useState<number>(initialCommentsToShow);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const loadComments = async (page = 0) => {
    try {
      setLoading(true);
      const response = await authenticatedGet(
        `${import.meta.env.VITE_API_URL}/comments/will/${willId}?page=${page}&size=20`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (page === 0) {
          setComments(data.content);
        } else {
          setComments(prevComments => [...prevComments, ...data.content]);
        }
        
        setHasMoreComments(!data.last);
        setCurrentPage(page);
      } else if (isAuthenticated) {
        toast.error("Failed to load comments");
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      if (isAuthenticated) {
        toast.error("Failed to load comments");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadComments();
    }
  }, [willId, isAuthenticated]);

  // Reset displayed comments when willId changes
  useEffect(() => {
    setDisplayedComments(initialCommentsToShow);
  }, [willId, initialCommentsToShow]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;
    
    if (!isAuthenticated) {
      toast.error("Please sign in to comment");
      navigate('/signin');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await authenticatedPost(
        `${import.meta.env.VITE_API_URL}/comments/will/${willId}`,
        { content: newComment.trim() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setComments(prevComments => [data, ...prevComments]);
        setNewComment('');
        toast.success("Comment added successfully");
      } else {
        toast.error("Failed to add comment");
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (id: string) => {
    if (!editText.trim()) return;
    
    try {
      setSubmitting(true);
      
      const response = await authenticatedPut(
        `${import.meta.env.VITE_API_URL}/comments/${id}`,
        { content: editText.trim() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setComments(prevComments => 
          prevComments.map(comment => 
            comment.id === id ? data : comment
          )
        );
        setEditingCommentId(null);
        toast.success("Comment updated successfully");
      } else {
        toast.error("Failed to update comment");
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error("Failed to update comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (id: string) => {
    try {
      setSubmitting(true);
      
      const response = await authenticatedDelete(
        `${import.meta.env.VITE_API_URL}/comments/${id}`
      );
      
      if (response.ok) {
        setComments(prevComments => prevComments.filter(comment => comment.id !== id));
        toast.success("Comment deleted successfully");
      } else {
        toast.error("Failed to delete comment");
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error("Failed to delete comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadMore = () => {
    loadComments(currentPage + 1);
  };

  const handleShowMoreComments = () => {
    setDisplayedComments(prev => prev + 5);
  };

  const showAllComments = () => {
    setDisplayedComments(comments.length);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h`;
    } else if (diffInSeconds < 2592000) {
      return `${Math.floor(diffInSeconds / 86400)}d`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Get the visible comments (most recent first)
  const visibleComments = comments.slice(0, displayedComments);
  const hasHiddenComments = comments.length > displayedComments;

  return (
    <div className={`bg-white flex flex-col h-full ${isModal ? '' : 'rounded-lg shadow-md'}`}>
      {!isModal && (
        <div className="p-3 border-b border-gray-200">
          <h3 className="text-lg font-medium">Comments</h3>
        </div>
      )}
      
      <div className={`flex-grow overflow-y-auto px-4 py-3 ${isModal ? 'h-full' : 'max-h-[300px]'}`}>
        {comments.length === 0 && !loading ? (
          <div className="text-center py-6 text-gray-500">
            {isAuthenticated ? (
              "No comments yet. Be the first to comment!"
            ) : (
              "Sign in to view and post comments."
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {hasHiddenComments && (
              <div className="text-center mb-2">
                <button
                  onClick={handleShowMoreComments}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center w-full p-2 bg-blue-50 rounded-lg"
                >
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Show previous comments 
                  <span className="ml-1 text-gray-500">({comments.length - displayedComments} more)</span>
                </button>
              </div>
            )}
            
            {visibleComments.map((comment) => (
              <div key={comment.id} className="flex space-x-3">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarFallback className="bg-gray-200 text-gray-600">
                    {comment.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-grow">
                  {editingCommentId === comment.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        rows={2}
                      />
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingCommentId(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEditComment(comment.id)}
                          disabled={submitting}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="font-medium text-sm mr-2">{comment.username}</span>
                          <span className="text-gray-500 text-xs">
                            {formatDate(comment.createdAt)}
                            {comment.updatedAt !== comment.createdAt && " (edited)"}
                          </span>
                        </div>
                        
                        {user && user.userId === comment.userId && (
                          <div className="flex space-x-1 text-gray-400">
                            <button
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditText(comment.content);
                              }}
                              className="p-1 hover:text-gray-700"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="p-1 hover:text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-sm">
                        {comment.content}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMoreComments && (
              <div className="text-center pt-2">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {loading ? "Loading..." : "Load more from server"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="border-t border-gray-200 p-3 mt-auto">
        <form onSubmit={handleSubmitComment} className="flex items-center space-x-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="flex-grow border rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            disabled={submitting || !isAuthenticated}
          />
          <button
            type="submit"
            disabled={submitting || !newComment.trim() || !isAuthenticated}
            className={`rounded-full bg-blue-600 p-2 text-white ${
              submitting || !newComment.trim() || !isAuthenticated
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-blue-700'
            }`}
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentSection; 